// Order management functions
import { collection, addDoc, getDocs, doc, updateDoc, query, where, orderBy, serverTimestamp, runTransaction } from 'firebase/firestore';
import { db } from './config';

/**
 * Create a new order
 */
export const createOrder = async (orderData) => {
  try {
    const ordersRef = collection(db, 'orders');
    
    const newOrder = {
      ...orderData,
      paymentStatus: 'pending',
      orderStatus: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(ordersRef, newOrder);
    
    // Track analytics
    await trackEvent('purchase', {
      orderId: docRef.id,
      total: orderData.total,
      productIds: orderData.items.map(item => item.productId)
    });
    
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update order payment status
 */
export const updateOrderPaymentStatus = async (orderId, paymentStatus, transactionId = null) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const updates = {
      paymentStatus,
      updatedAt: serverTimestamp()
    };
    
    if (transactionId) {
      updates.transactionId = transactionId;
    }
    
    if (paymentStatus === 'completed') {
      updates.orderStatus = 'processing';
    }
    
    await updateDoc(orderRef, updates);
    
    // If payment completed, trigger email sequence and deliver digital products
    if (paymentStatus === 'completed') {
      const orderDoc = await getDoc(orderRef);
      const orderData = { id: orderDoc.id, ...orderDoc.data() };
      
      // Trigger email sequence
      await triggerEmailSequence('purchase', orderData);
      
      // Deliver digital products
      try {
        const { deliverDigitalProducts } = await import('./stripe');
        await deliverDigitalProducts(orderData.id);
      } catch (deliveryError) {
        if (import.meta.env.DEV) {
          console.error('Error delivering digital products:', deliveryError);
        }
        // Don't fail the order update if delivery fails - log it
        await updateDoc(orderRef, {
          deliveryError: deliveryError.message,
          updatedAt: serverTimestamp()
        });
      }
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error updating order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all orders
 */
export const getAllOrders = async (filters = {}) => {
  try {
    const ordersRef = collection(db, 'orders');
    let q = query(ordersRef, orderBy('createdAt', 'desc'));
    
    if (filters.paymentStatus) {
      q = query(q, where('paymentStatus', '==', filters.paymentStatus));
    }
    
    if (filters.orderStatus) {
      q = query(q, where('orderStatus', '==', filters.orderStatus));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting orders:', error);
    return [];
  }
};

/**
 * Get orders for a specific customer
 */
export const getCustomerOrders = async (customerEmail) => {
  try {
    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef,
      where('customerEmail', '==', customerEmail),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting customer orders:', error);
    return [];
  }
};

/**
 * Track analytics event
 */
export const trackEvent = async (type, metadata = {}) => {
  try {
    const analyticsRef = collection(db, 'analytics');
    await addDoc(analyticsRef, {
      type,
      metadata,
      timestamp: serverTimestamp(),
      sessionId: sessionStorage.getItem('sessionId') || `session_${Date.now()}`,
      userId: localStorage.getItem('userId') || null
    });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Trigger email sequence
 */
export const triggerEmailSequence = async (triggerType, orderData) => {
  try {
    // Import email functions dynamically to avoid circular dependencies
    const { triggerEmailSequence: triggerSequence } = await import('./email');
    return await triggerSequence(triggerType, {
      email: orderData.customerEmail,
      name: orderData.customerName || 'Customer',
      variables: {
        orderId: orderData.id,
        total: orderData.total,
        items: orderData.items?.map(item => item.productTitle).join(', ') || ''
      }
    });
  } catch (error) {
    console.error('Error triggering email sequence:', error);
  }
};

